import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import {
  Box, Stack, Typography, Button, TextField, InputAdornment,
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, IconButton, Menu, MenuItem, Chip, Alert,
  FormControl, InputLabel, Select, Skeleton, alpha, useTheme,
  ListItemIcon, ListItemText, Tooltip,
} from '@mui/material';
import {
  Search, MoreVert, HowToReg, PowerSettingsNew,
  OpenInNew, School,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import feeAssignmentService from '../../services/feeAssignmentService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SearchableSelect from '../../components/common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import BulkAssignFeesDialog from '../../components/fees/BulkAssignFeesDialog';
import { getHashedColor } from '../../utils/colorUtils';

import { FeeAssignmentRow, FeeAssignmentStatus } from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

const DEFAULT_ROWS_PER_PAGE = 25;

const STATUS_LABEL: Record<FeeAssignmentStatus, string> = {
  active: 'Active',
  suspended: 'Suspended',
  closed: 'Closed',
};

const FeeAssignmentsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const canReadFees = canRead('fees');
  const canWriteFees = canWrite('fees');

  const urlSession = searchParams.get('sessionId') ?? '';
  const urlClass = searchParams.get('classId') ?? '';
  const urlStatus = (searchParams.get('status') as FeeAssignmentStatus) ?? 'active';
  const urlSearch = searchParams.get('search') ?? '';
  const urlPage = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const [sessionId, setSessionId] = useState(urlSession);
  const [classId, setClassId] = useState(urlClass);
  const [status, setStatus] = useState<FeeAssignmentStatus | 'any'>(urlStatus);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(urlPage - 1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [rows, setRows] = useState<FeeAssignmentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Skeleton only on the initial mount. Subsequent loads keep old rows visible.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);

  // Dialogs
  const [assignOpen, setAssignOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<FeeAssignmentRow | null>(null);
  const [closeReason, setCloseReason] = useState('');

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<FeeAssignmentRow | null>(null);

  // ─── URL sync ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const next = new URLSearchParams();
    if (sessionId) next.set('sessionId', sessionId);
    if (classId) next.set('classId', classId);
    if (status && status !== 'active') next.set('status', status);
    if (search) next.set('search', search);
    if (page > 0) next.set('page', String(page + 1));
    setSearchParams(next, { replace: true });
  }, [sessionId, classId, status, search, page, setSearchParams]);

  // ─── Load session + class pickers once ────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        if (!sessionId) {
          const active = ss.find(s => s.status === 'active') ?? ss[0];
          if (active) setSessionId(active._id);
        }
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load pickers', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load assignments ────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feeAssignmentService.listFeeAssignments({
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        status,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(res.assignments);
      setTotal(res.pagination.totalItems);
      setHasLoadedOnce(true);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load assignments', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, classId, status, search, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: FeeAssignmentRow) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };
  const openClose = (row: FeeAssignmentRow) => {
    setCloseTarget(row);
    setCloseReason('');
    setCloseOpen(true);
    closeMenu();
  };
  const confirmClose = async () => {
    if (!closeTarget) return;
    try {
      await feeAssignmentService.closeFeeAssignment(closeTarget._id, closeReason.trim() || undefined);
      enqueueSnackbar(`Closed assignment for ${closeTarget.student.name}`, { variant: 'success' });
      setCloseOpen(false);
      setCloseTarget(null);
      load();
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to close assignment', { variant: 'error' });
    }
  };

  const currencyFmt = useCurrencyFormat();

  const scopeLabel = (row: FeeAssignmentRow) =>
    row.section?.name ? `${row.class.name ?? '—'} – ${row.section.name}` : (row.class.name ?? '—');

  const skeletonRows = useMemo(() =>
    Array.from({ length: 6 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell><Skeleton variant="text" width={24} /></TableCell>
        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
        <TableCell><Skeleton variant="text" width="40%" /></TableCell>
        <TableCell><Skeleton variant="text" width="50%" /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
        <TableCell><Skeleton variant="text" width={60} /></TableCell>
        <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
      </TableRow>
    )),
    []);

  const resetFilters = () => {
    setClassId('');
    setStatus('active');
    setSearchInput('');
    setPage(0);
  };

  if (!canReadFees) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">You don't have permission to view fee assignments.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>Fee Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            Each row is one student's frozen copy of a plan. Bulk-assign a plan to a class to bill every enrolled student in one click.
          </Typography>
        </Box>
        {canWriteFees && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<HowToReg />}
            onClick={() => setAssignOpen(true)}
            sx={{ borderRadius: 2, alignSelf: 'flex-start' }}
          >
            Bulk assign
          </Button>
        )}
      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <SearchableSelect
            label="Session"
            value={sessionId}
            onChange={v => { setSessionId(v); setPage(0); }}
            options={buildSessionOptions(sessions, theme)}
            placeholder="All sessions"
            minWidth={200}
          />
          <SearchableSelect
            label="Class"
            value={classId}
            onChange={v => { setClassId(v); setPage(0); }}
            options={classes.map(c => ({ value: c._id, label: c.name }))}
            placeholder="All classes"
            minWidth={200}
          />
          <SearchableSelect
            label="Status"
            value={status}
            onChange={v => { setStatus((v || 'active') as any); setPage(0); }}
            options={[
              { value: 'active',    label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'closed',    label: 'Closed' },
              { value: 'any',       label: 'All statuses' },
            ]}
            clearable={false}
            minWidth={160}
          />
          <TextField
            size="small"
            placeholder="Search by name, GR number or roll…"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              ),
            }}
          />
          {(classId || status !== 'active' || search) && (
            <Button size="small" onClick={resetFilters}>Reset</Button>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell sx={{ width: 60 }}>Roll</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Cohort</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="right">Annual total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ pr: 2 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !hasLoadedOnce ? (
              skeletonRows
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <HowToReg sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                    {classId || status !== 'active' || search ? 'No assignments match these filters' : 'No fee assignments yet'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {canWriteFees ? 'Pick a plan and a class from Bulk assign to start billing a cohort.' : 'Ask an admin to bulk-assign a plan to a class.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => {
                const sectionColor = row.section?.name
                  ? getHashedColor(String(row.section._id || row.section.name), theme.palette.mode === 'dark')
                  : null;
                return (
                  <TableRow key={row._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {row.rollNumber ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.student.name}</Typography>
                      {row.student.grNumber && (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', display: 'block' }}>
                          {row.student.grNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="body2" fontWeight={600}>
                          {row.class.name ?? '—'}
                        </Typography>
                        {row.section?.name && sectionColor && (
                          <Chip
                            label={row.section.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              letterSpacing: 0.2,
                              borderColor: alpha(sectionColor.text, 0.35),
                              color: sectionColor.text,
                              bgcolor: sectionColor.bg,
                              '& .MuiChip-label': { px: 0.9 },
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.plan.name ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.itemCount} line item{row.itemCount === 1 ? '' : 's'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {currencyFmt(row.totalAnnualAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABEL[row.status]}
                        variant="outlined"
                        sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 500,
                          ...(row.status === 'active'
                            ? {
                                borderColor: alpha(theme.palette.success.main, 0.32),
                                color: theme.palette.success.dark,
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                              }
                            : row.status === 'suspended'
                              ? {
                                  borderColor: alpha(theme.palette.warning.main, 0.32),
                                  color: theme.palette.warning.dark,
                                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                                }
                              : {
                                  borderColor: alpha(theme.palette.text.primary, 0.16),
                                  color: 'text.secondary',
                                }),
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <IconButton size="small" onClick={e => openMenu(e, row)} aria-label="Row actions">
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} {...elegantMenuWithHintsProps} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem disabled>
          <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
          <ListItemText primary="View details" secondary="Coming with the student drawer's Fees tab" />
        </MenuItem>
        {canWriteFees && menuRow?.status === 'active' && (
          <MenuItem onClick={() => menuRow && openClose(menuRow)} sx={{ color: 'error.main' }}>
            <ListItemIcon><PowerSettingsNew fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Close assignment" />
          </MenuItem>
        )}
      </Menu>

      {/* Bulk-assign dialog */}
      <BulkAssignFeesDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => { load(); }}
      />

      {/* Close-assignment confirm */}
      <ConfirmationDialog
        open={closeOpen}
        title="Close fee assignment?"
        message={closeTarget ? `${closeTarget.student.name} will stop receiving new challans from “${closeTarget.plan.name ?? 'this plan'}”. Existing challans stay for collection.` : ''}
        confirmText="Close assignment"
        confirmColor="error"
        onConfirm={confirmClose}
        onClose={() => setCloseOpen(false)}
      >
        <TextField
          fullWidth
          size="small"
          label="Reason (optional)"
          placeholder="e.g. Withdrawn from school"
          value={closeReason}
          onChange={e => setCloseReason(e.target.value)}
          sx={{ mt: 1 }}
          multiline
          minRows={2}
        />
      </ConfirmationDialog>
    </Box>
  );
};

export default FeeAssignmentsPage;
